// Login. Production: Keycloak (OpenID Connect, authorization code + PKCE) via keycloak-js.
// Development (the API runs with PRAYOG_AUTH_MODE=dev): a simple e-mail form that asks the API for a dev token.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type Keycloak from "keycloak-js";
import { API, configureApi } from "./api";

export interface AuthConfig {
  mode: "oidc" | "dev";
  issuer: string;
  client_id: string;
}

interface AuthState {
  ready: boolean;
  loggedIn: boolean;
  mode: AuthConfig["mode"] | null;
  error: string | null;
  devLogin: (email: string, name: string, admin: boolean, organization?: string, country?: string) => Promise<void>;
  /** Production: go to the Keycloak login page; afterwards the browser comes back to `returnTo` (default: this page). */
  login: (returnTo?: string) => void;
  logout: () => void;
  accountUrl: string | null;
}

const AuthContext = createContext<AuthState | null>(null);
const DEV_TOKEN_KEY = "prayog.devToken";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** "https://host/auth/realms/prayog" -> { url: "https://host/auth", realm: "prayog" } */
export function splitIssuer(issuer: string): { url: string; realm: string } {
  const i = issuer.indexOf("/realms/");
  if (i < 0) throw new Error(`Unexpected OIDC issuer: ${issuer}`);
  return { url: issuer.slice(0, i), realm: issuer.slice(i + "/realms/".length).replace(/\/$/, "") };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [mode, setMode] = useState<AuthConfig["mode"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accountUrl, setAccountUrl] = useState<string | null>(null);
  const kc = useRef<Keycloak | null>(null);
  const devToken = useRef<string | null>(storage()?.getItem(DEV_TOKEN_KEY) ?? null);

  const logout = useCallback(() => {
    if (kc.current) {
      kc.current.logout({ redirectUri: window.location.origin + "/" });
      return;
    }
    devToken.current = null;
    storage()?.removeItem(DEV_TOKEN_KEY);
    setLoggedIn(false);
    window.location.assign("/"); // back to the landing page, like the production logout
  }, []);

  const login = useCallback((returnTo?: string) => {
    kc.current?.login(returnTo ? { redirectUri: window.location.origin + returnTo } : undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/config`);
        if (!res.ok) throw new Error(`The server answered ${res.status}.`);
        const cfg: AuthConfig = await res.json();
        if (cancelled) return;
        setMode(cfg.mode);
        if (cfg.mode === "oidc") {
          const { default: KeycloakCls } = await import("keycloak-js");
          const { url, realm } = splitIssuer(cfg.issuer);
          const client = new KeycloakCls({ url, realm, clientId: cfg.client_id });
          kc.current = client;
          // "check-sso": find out whether the user is logged in without forcing the login page - visitors who are not
          // logged in see the landing page (about.html) first; the login page comes with its "Log in" button (/login).
          const ok = await client.init({ onLoad: "check-sso", pkceMethod: "S256", checkLoginIframe: false });
          configureApi(
            async () => {
              try {
                await client.updateToken(30);
              } catch {
                client.login();
                return null;
              }
              return client.token ?? null;
            },
            () => client.login(),
          );
          setAccountUrl(`${url}/realms/${realm}/account`);
          setLoggedIn(ok);
        } else {
          configureApi(
            async () => devToken.current,
            () => {
              devToken.current = null;
              storage()?.removeItem(DEV_TOKEN_KEY);
              setLoggedIn(false);
            },
          );
          setLoggedIn(!!devToken.current);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const devLogin = useCallback(async (email: string, name: string, admin: boolean, organization = "", country = "") => {
    const res = await fetch(`${API}/auth/dev-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, name, admin, organization, country }),
    });
    if (!res.ok) throw new Error("Login failed.");
    const { access_token } = await res.json();
    devToken.current = access_token;
    storage()?.setItem(DEV_TOKEN_KEY, access_token);
    setLoggedIn(true);
  }, []);

  const value = useMemo(
    () => ({ ready, loggedIn, mode, error, devLogin, login, logout, accountUrl }),
    [ready, loggedIn, mode, error, devLogin, login, logout, accountUrl],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
